// Marqueurs d'effets dans les textes de dialogue (PNJ, quêtes, pancartes, répliques des {monstres} posés). Les effets
// se jouent au moment où l'écriture du texte les atteint : « {boom} » secoue l'écran avec un bruit, « {flash} »
// l'éblouit, « {éclair} » fait gronder l'orage, « {noir} » le plonge un instant dans le noir, « {pause} » suspend
// l'écriture. Les styles mettent en forme les mots qu'ils entourent : « {tremble}…{/tremble} », « {gros}…{/gros} ».
// Partagé par le jeu (qui les joue) et l'administration (aperçu des textes).

export const TEXT_EFFECTS = {
  boom: { label: 'Secousse', description: 'Secoue l\'écran du joueur avec un bruit d\'explosion' },
  flash: { label: 'Flash', description: 'Éblouit l\'écran du joueur d\'un flash blanc' },
  'éclair': { label: 'Éclair', description: 'Double éclair blanc et grondement de tonnerre' },
  noir: { label: 'Noir', description: 'Fondu au noir d\'un instant, puis le texte reprend (ellipse, évanouissement…)' },
  pause: { label: 'Pause', description: 'Suspend l\'écriture une seconde (suspense)' },
} as const satisfies Record<string, { label: string; description: string }>;
export type TextEffect = keyof typeof TEXT_EFFECTS;
export const TEXT_EFFECT_KEYS = Object.keys(TEXT_EFFECTS) as TextEffect[];

/** Styles : ils s'appliquent aux mots entre `{style}` et `{/style}` (jusqu'à la fin de la page sans fermeture). */
export const TEXT_STYLES = {
  tremble: { label: 'Tremble', description: 'Les mots entourés tremblent (peur, froid, colère)' },
  gros: { label: 'Gros', description: 'Les mots entourés s\'écrivent en grand (cri)' },
} as const satisfies Record<string, { label: string; description: string }>;
export type TextStyle = keyof typeof TEXT_STYLES;
export const TEXT_STYLE_KEYS = Object.keys(TEXT_STYLES) as TextStyle[];

/** Orthographes acceptées en plus (sans accent). */
const ALIASES: Readonly<Record<string, TextEffect>> = { eclair: 'éclair' };

/** Un effet et sa place dans le texte sans marqueurs (il se joue quand l'écriture atteint ce caractère). */
export interface PlacedTextEffect {
  effect: TextEffect;
  at: number;
}

/** Un style et les caractères qu'il couvre dans le texte sans marqueurs (`from` inclus, `to` exclu). */
export interface StyledRange {
  style: TextStyle;
  from: number;
  to: number;
}

export interface ScriptedText {
  text: string;
  effects: PlacedTextEffect[];
  styles: StyledRange[];
}

const NAMES = [...TEXT_EFFECT_KEYS, ...Object.keys(ALIASES), ...TEXT_STYLE_KEYS];
const TOKEN = new RegExp(`\\{(/?)(${NAMES.join('|')})\\}`, 'gi');

const isStyle = (name: string): name is TextStyle => (TEXT_STYLE_KEYS as string[]).includes(name);
const effectOf = (name: string): TextEffect | null => ALIASES[name] ?? ((TEXT_EFFECT_KEYS as string[]).includes(name) ? name as TextEffect : null);

/**
 * Sépare un texte de ses marqueurs : effets (« Attention… {boom} Trop tard ! ») et styles (« {gros}Stop !{/gros} ») ;
 * les majuscules ne comptent pas. Un marqueur de fermeture sans ouverture, ou de fermeture d'un effet, est ignoré.
 */
export function splitTextEffects(text: string): ScriptedText {
  const effects: PlacedTextEffect[] = [];
  const styles: StyledRange[] = [];
  const open = new Map<TextStyle, number>();
  let clean = '';
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    clean += text.slice(last, match.index);
    last = match.index + match[0].length;
    const closing = match[1] === '/';
    const name = match[2]!.toLowerCase();
    if (isStyle(name)) {
      const from = open.get(name);
      if (!closing && from === undefined) open.set(name, clean.length);
      else if (closing && from !== undefined) {
        open.delete(name);
        if (clean.length > from) styles.push({ style: name, from, to: clean.length });
      }
      continue;
    }
    const effect = effectOf(name);
    if (closing || !effect) continue;
    // Pas de double espace là où était le marqueur.
    if (clean.endsWith(' ') && text.charAt(last) === ' ') last++;
    effects.push({ effect, at: clean.length });
  }
  clean += text.slice(last);
  for (const [style, from] of open) if (clean.length > from) styles.push({ style, from, to: clean.length });
  return { text: clean, effects, styles: styles.sort((a, b) => a.from - b.from) };
}
